import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/components/shadcn/dialog';
import { Button } from '@/shared/ui/components/shadcn/button';
import { Text } from '@/shared/ui';
import Badge from '@/shared/ui/components/basic/Badge';
import type { Vistory } from '../types/vistory.types';
import type { PropertyNestedResponse } from '@services/housien/types-property.d';
import { VistoryService } from '../services/vistory-service';
import { PropertiesService } from '@services/requests/housien-services/PropertiesService';
import { VistoryPhotoUploader } from './VistoryPhotoUploader';
import { VistoryPhotoGallery } from './VistoryPhotoGallery';
import { VistoryRoomView } from './VistoryRoomView';
import { VistoryRoomEditor } from './VistoryRoomEditor';
import { EditableFields, ReadOnlyFields, RejectionNotice } from './VistoryDetailFields';
import { SendApprovalDialog } from './SendApprovalDialog';

interface Props {
  vistory: Vistory;
  agencyId: string;
  onClose: () => void;
  onUpdate: () => void;
}

const STATUS_BADGE: Record<string, { color: string; bg: string }> = {
  draft: { color: 'var(--status-info)', bg: 'var(--status-info-subtle)' },
  pending_approval: { color: 'var(--status-warning)', bg: 'var(--status-warning-subtle)' },
  approved: { color: 'var(--status-success)', bg: 'var(--status-success-subtle)' },
  rejected: { color: 'var(--status-error)', bg: 'var(--status-error-subtle)' },
};

/**
 * Detail dialog for a vistory: view/edit fields, manage photos, rooms, and
 * submit for approval when the vistory is still a draft.
 */
export function VistoryDetailPanel({ vistory, agencyId, onClose, onUpdate }: Props) {
  const { t } = useTranslation('features/vistories');
  const [title, setTitle] = useState(vistory.title);
  const [description, setDescription] = useState(vistory.description ?? '');
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [draftReport, setDraftReport] = useState<string | null>(vistory.draft_report ?? null);
  const [property, setProperty] = useState<PropertyNestedResponse | null>(null);
  const [showSendDialog, setShowSendDialog] = useState(false);

  useEffect(() => {
    if (vistory.property_id) {
      PropertiesService.getProperty(agencyId, vistory.property_id)
        .then(setProperty)
        .catch(() => setProperty(null));
    }
  }, [agencyId, vistory.property_id]);

  const isDraft = vistory.status === 'draft';
  // C3 fix: a rejected vistory is editable and resendable, same as a
  // draft — the broker acts on the tenant's feedback and resends
  // without any extra transition back to draft first.
  const isEditable = isDraft || vistory.status === 'rejected';
  const canSend = isEditable && vistory.photos.length > 0;
  const canGenerateReport = vistory.photos.length > 0;
  const statusTone = STATUS_BADGE[vistory.status] ?? STATUS_BADGE.draft;

  async function handleSave() {
    setSaving(true);
    try {
      await VistoryService.updateVistory(agencyId, vistory.id, { title, description });
      onUpdate();
    } finally { setSaving(false); }
  }

  async function handleGenerateReport() {
    setGenerating(true);
    try {
      const res = await VistoryService.generateReport(agencyId, vistory.id);
      setDraftReport(res.draft);
    } finally { setGenerating(false); }
  }

  return (
    <>
      <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center gap-[var(--space-3)]">
              <DialogTitle className="flex-1">{vistory.title}</DialogTitle>
              {vistory.type && (
                <Badge color="var(--text-secondary)" backgroundColor="var(--surface-tertiary)" size="sm">
                  {t(`vistory.type_${vistory.type}`)}
                </Badge>
              )}
              <Badge color={statusTone.color} backgroundColor={statusTone.bg} size="sm">
                {t(`vistory.status.${vistory.status}`)}
              </Badge>
            </div>
          </DialogHeader>
          <VistoryDetailBody
            vistory={vistory} agencyId={agencyId} isEditable={isEditable}
            title={title} setTitle={setTitle} description={description}
            setDescription={setDescription} property={property}
            draftReport={draftReport} onUpdate={onUpdate} t={t}
          />
          <ActionButtons
            canGenerateReport={canGenerateReport} generating={generating}
            onGenerate={handleGenerateReport}
            isEditable={isEditable} saving={saving} onSave={handleSave}
            canSend={canSend} onOpenSend={() => setShowSendDialog(true)} t={t}
          />
        </DialogContent>
      </Dialog>
      {showSendDialog && (
        <SendApprovalDialog
          agencyId={agencyId} vistoryId={vistory.id} open
          onClose={() => setShowSendDialog(false)}
          onSent={() => { onUpdate(); onClose(); }}
        />
      )}
    </>
  );
}

/**
 * Body of the vistory detail panel: editable fields, rooms, photos, and report.
 */
function VistoryDetailBody({ vistory, agencyId, isEditable, title, setTitle, description, setDescription, property, draftReport, onUpdate, t }: {
  vistory: Vistory; agencyId: string; isEditable: boolean;
  title: string; setTitle: (v: string) => void;
  description: string; setDescription: (v: string) => void;
  property: PropertyNestedResponse | null;
  draftReport: string | null; onUpdate: () => void; t: (k: string) => string;
}) {
  return (
    <div className="flex flex-col gap-[var(--space-4)]">
      {isEditable
        ? <EditableFields title={title} setTitle={setTitle} description={description} setDescription={setDescription} t={t} />
        : <ReadOnlyFields title={vistory.title} description={vistory.description} />}
      <VistoryPhotoGallery vistory={vistory} agencyId={agencyId} />
      {isEditable && <VistoryPhotoUploader agencyId={agencyId} vistoryId={vistory.id} onUploaded={onUpdate} />}
      {isEditable
        ? <VistoryRoomEditor vistory={vistory} agencyId={agencyId} property={property} onUpdate={onUpdate} />
        : (vistory.rooms?.length ?? 0) > 0 && <VistoryRoomView rooms={vistory.rooms!} />}
      {vistory.status === 'rejected' && vistory.rejection_reason && (
        <RejectionNotice
          reason={vistory.rejection_reason}
          label={t('vistory.rejection_reason')}
          appointments={vistory.proposed_appointments}
        />
      )}
      {draftReport && (
        <div className="rounded-(--radius-md) border border-[var(--border-subtle)] bg-[var(--surface-secondary)] p-[var(--space-4)]">
          <Text size="xs" fw={500} className="text-[var(--text-secondary)] mb-[var(--space-2)]">
            {t('vistory.generated_report')}
          </Text>
          <Text size="sm" className="text-[var(--text-primary)] whitespace-pre-wrap">{draftReport}</Text>
        </div>
      )}
    </div>
  );
}

/**
 * Action buttons for a vistory: generate report/summaries, save draft, and send.
 */
function ActionButtons({ canGenerateReport, generating, onGenerate, isEditable, saving, onSave, canSend, onOpenSend, t }: {
  canGenerateReport: boolean; generating: boolean; onGenerate: () => void;
  isEditable: boolean; saving: boolean; onSave: () => void;
  canSend: boolean; onOpenSend: () => void; t: (k: string) => string;
}) {
  return (
    <div className="flex flex-wrap justify-end gap-[var(--space-2)]">
      {canGenerateReport && (
        <Button variant="outline" onClick={onGenerate} disabled={generating}>{t('vistory.generate_report')}</Button>
      )}
      {isEditable && <Button variant="outline" onClick={onSave} disabled={saving}>{t('vistory.save')}</Button>}
      {canSend && <Button onClick={onOpenSend}>{t('vistory.send_approval')}</Button>}
    </div>
  );
}
